import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Minus, Edit, Trash2, Package, Search, BookOpen, QrCode,
  Footprints, Shirt, Shield, Hand, Anchor, HardHat, Ear, Glasses, Wind,
  FileText, Download, Flame, Zap, CloudRain, Loader2, Upload, UserX, AlertTriangle,
  ClipboardList,
} from "lucide-react";

function iconForItemName(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("antipioggia") || n.includes("cayenne")) return CloudRain;
  if (n.includes("flame") || n.includes("ignifug")) return Flame;
  if (n.includes("polytech") || n.includes("antistatic")) return Zap;
  if (n.includes("ffp") || n.includes("semimaschera")) return Wind;
  if (n.includes("casco")) return HardHat;
  if (n.includes("cuffie")) return Ear;
  if (n.includes("occhiali")) return Glasses;
  if (n.includes("guanti")) return Hand;
  if (n.includes("scarpe") || n.includes("stivali") || n.includes("soprascarpe")) return Footprints;
  if (n.includes("imbracatura") || n.includes("cordino")) return Anchor;
  if (n.includes("maschera")) return Shield;
  if (n.includes("grembiule") || n.includes("manicotti")) return Shield;
  if (n.includes("gilet") || n.includes("giacca") || n.includes("pantalone") || n.includes("polo") || n.includes("felpa") || n.includes("tuta")) return Shirt;
  return Package;
}
function ItemIcon({ name, className }) {
  const Cmp = iconForItemName(name);
  return <Cmp className={className} />;
}
import { useAuth } from "@/context/AuthContext";
import { puoGestire } from "@/lib/ruoli";
import { DEFAULT_ITEM_PHOTO, photoUrl } from "@/lib/placeholders";
import BottoneFoto from "@/components/BottoneFoto";

const CATEGORIES = ["Protezione piedi", "Alta visibilità", "Saldatura", "Mani", "Cadute",
                    "Capo", "Indumenti protettivi", "Antipioggia", "Non DPI"];

// La categoria del DPI secondo il Regolamento (UE) 2016/425: dice quanto e'
// serio il rischio da cui protegge, e cambia gli obblighi di formazione.
const CATEGORIA_DPI = {
  I: { nome: "Cat. I", spiega: "Rischi minimi", classe: "bg-slate-100 text-slate-600 border-slate-200" },
  II: { nome: "Cat. II", spiega: "Rischi significativi", classe: "bg-amber-100 text-amber-800 border-amber-200" },
  III: { nome: "Cat. III", spiega: "Rischi gravi o mortali", classe: "bg-red-100 text-red-700 border-red-200" },
};

const euro = (v) => (v === null || v === undefined ? null
  : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" }));

export default function Magazzino() {
  const { user } = useAuth();
  // Articoli, giacenze e import sono `require_gestione`: amministrazione dentro.
  const isAdmin = puoGestire(user);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [adjustItem, setAdjustItem] = useState(null);
  const [qrItem, setQrItem] = useState(null);
  const [schedaItem, setSchedaItem] = useState(null);
  const [showCatalogo, setShowCatalogo] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showInventario, setShowInventario] = useState(false);

  const load = async () => {
    const { data } = await api.get("/items");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // Arrivo dallo scanner QR: mostra solo l'articolo inquadrato.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedId = searchParams.get("articolo");
  useEffect(() => {
    if (!wantedId || items.length === 0) return;
    const it = items.find(i => i.id === wantedId);
    if (it) { setFilter(it.code); setCatFilter("all"); }
    else toast.error("Articolo non trovato");
    searchParams.delete("articolo");
    setSearchParams(searchParams, { replace: true });
  }, [wantedId, items, searchParams, setSearchParams]);

  const filtered = items.filter(i =>
    (catFilter === "all" || i.category === catFilter) &&
    (i.name.toLowerCase().includes(filter.toLowerCase()) || i.code.toLowerCase().includes(filter.toLowerCase()))
  );

  const onSubmit = async (form) => {
    try {
      if (editing) {
        await api.put(`/items/${editing.id}`, form);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/items", form);
        toast.success("Articolo creato");
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Disattivare questo articolo?")) return;
    await api.delete(`/items/${id}`);
    load();
  };

  const onAdjust = async (size, delta) => {
    try {
      await api.post(`/items/${adjustItem.id}/adjust`, { size, delta });
      toast.success(delta > 0 ? "Carico effettuato" : "Scarico effettuato");
      const { data } = await api.get("/items");
      setItems(data);
      setAdjustItem(data.find(i => i.id === adjustItem.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="magazzino-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">DPI</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Magazzino DPI</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Il foglio di conta lo stampa chi va davanti allo scaffale, non
              solo l'ufficio: resta fuori dal gruppo riservato. Ricaricare le
              conte invece sta dentro il dialogo, per chi puo' gestire. */}
          <Button variant="outline" className="h-12" onClick={() => setShowInventario(true)}
                  data-testid="inventario-btn">
            <ClipboardList className="w-4 h-4 mr-2" /> Inventario
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap" style={{ display: isAdmin ? undefined : "none" }}>
          <Button variant="outline" className="h-12" onClick={() => setShowImport(true)}
                  data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" /> Importa da CSV
          </Button>
          <Button variant="outline" className="h-12" onClick={() => setShowCatalogo(true)}
                  data-testid="catalogo-btn">
            <Download className="w-4 h-4 mr-2" /> Catalogo fornitore
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12"
                  onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo articolo
          </Button>
        </div>
      </div>

      {/* Finche' nessun articolo ha la scheda tecnica, il pulsante che la apre
          non esiste: senza dirlo, si cerca invano. */}
      {isAdmin && items.length > 0 && !items.some(i => i.scheda) && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <FileText className="w-5 h-5 text-orange-600 flex-shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <div className="font-semibold text-sm">Nessun articolo ha ancora la scheda tecnica</div>
              <div className="text-xs text-slate-600">
                Le schede — norme, categoria del DPI, materiali, foto e prezzi — arrivano
                dal catalogo del fornitore. Il pulsante &quot;Scheda tecnica&quot; compare
                sugli articoli dopo l&apos;importazione.
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowCatalogo(true)}>
              Importa il catalogo
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca articolo o codice..." className="pl-9 h-11"
                 value={filter} onChange={(e)=>setFilter(e.target.value)} data-testid="search-input" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[200px] h-11" data-testid="cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(it => {
          const totalStock = Object.values(it.stock || {}).reduce((s, v) => s + v, 0);
          const isLow = totalStock <= (it.min_stock || 0);
          return (
            <Card key={it.id} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  {it.photo_url ? (
                    <img src={photoUrl(it.photo_url)} alt={it.name}
                         className="w-16 h-16 rounded object-cover border border-slate-200 flex-shrink-0 bg-slate-50" />
                  ) : (
                    <div className="w-16 h-16 rounded border border-slate-200 flex-shrink-0 bg-orange-50 flex items-center justify-center">
                      <ItemIcon name={it.name} className="w-7 h-7 text-orange-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500 font-mono">{it.code}</div>
                    <div className="font-display font-bold text-base leading-tight">{it.name}</div>
                    <Badge variant="outline" className="mt-1">{it.category}</Badge>
                  </div>
                  <Package className={`w-5 h-5 flex-shrink-0 ${isLow ? "text-red-500" : "text-slate-400"}`} />
                </div>
                {it.scheda ? (
                  <div className="mt-2 flex flex-wrap gap-1 items-center">
                    {it.scheda.categoria_dpi && CATEGORIA_DPI[it.scheda.categoria_dpi] && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${CATEGORIA_DPI[it.scheda.categoria_dpi].classe}`}
                            title={CATEGORIA_DPI[it.scheda.categoria_dpi].spiega}>
                        {CATEGORIA_DPI[it.scheda.categoria_dpi].nome}
                      </span>
                    )}
                    {(it.scheda.norme || []).slice(0, 3).map(n => (
                      <span key={n} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600">
                        {n}
                      </span>
                    ))}
                    {(it.scheda.norme || []).length > 3 && (
                      <span className="text-[10px] text-slate-400">+{it.scheda.norme.length - 3}</span>
                    )}
                    {it.scheda.prezzo !== null && it.scheda.prezzo !== undefined && (
                      <span className="text-[10px] text-slate-500 ml-auto tabular-nums">
                        {euro(it.scheda.prezzo)}
                      </span>
                    )}
                  </div>
                ) : it.normative ? (
                  <div className="mt-2 text-[10px] text-slate-500 flex items-start gap-1 leading-tight">
                    <BookOpen className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{it.normative}</span>
                  </div>
                ) : null}
                <div className="mt-3 grid grid-cols-4 gap-1 text-center text-xs">
                  {it.sizes.map(s => {
                    const q = it.stock?.[s] || 0;
                    return <div key={s} className={`p-2 rounded ${q <= it.min_stock ? "bg-red-50 border border-red-200" : "bg-slate-50"}`}>
                      <div className="text-[10px] uppercase text-slate-500">{s}</div>
                      <div className="font-bold">{q}</div>
                    </div>;
                  })}
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  {it.scheda && (
                    <Button variant="outline" size="sm" onClick={() => setSchedaItem(it)}
                            data-testid={`scheda-${it.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Scheda tecnica
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setQrItem(it)} title="QR">
                    <QrCode className="w-3 h-3" />
                  </Button>
                </div>
                {isAdmin && (
                  <div className="mt-2 flex justify-end gap-2">
                    <BottoneFoto url={`/items/${it.id}/photo`} onCaricata={load}
                                 testId={`foto-${it.id}`} />
                    <Button variant="outline" size="sm" onClick={() => setAdjustItem(it)} data-testid={`adjust-${it.id}`}>
                      <Plus className="w-3 h-3" /><Minus className="w-3 h-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(it); setShowForm(true); }}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onDelete(it.id)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {schedaItem && <SchedaTecnica item={schedaItem} onClose={() => setSchedaItem(null)} />}
      {showCatalogo && (
        <DialogCatalogo onClose={() => setShowCatalogo(false)}
                        onImportato={() => { setShowCatalogo(false); load(); }} />
      )}
      {showInventario && (
        <DialogInventario puoCaricare={isAdmin}
                          onClose={() => setShowInventario(false)}
                          onImportato={load} />
      )}
      {showImport && (
        <DialogImportCsv onClose={() => setShowImport(false)}
                         onImportato={load} />
      )}
      {showForm && <ItemForm initial={editing} onSubmit={onSubmit} onClose={() => { setShowForm(false); setEditing(null); }} />}
      {adjustItem && <AdjustDialog item={adjustItem} onClose={() => setAdjustItem(null)} onAdjust={onAdjust} />}
      {qrItem && (
        <Dialog open onOpenChange={() => setQrItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>QR per taglia · {qrItem.name}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 mb-2">
              Un QR distinto per ogni taglia: stampalo e attaccalo sullo scaffale/contenitore corrispondente.
              Durante la consegna, scansionandolo si seleziona automaticamente l'articolo e la taglia giusti.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {(qrItem.sizes || []).map(s => (
                <div key={s} className="border border-slate-200 rounded p-3 text-center">
                  <div className="font-bold text-sm mb-1">Taglia {s}</div>
                  <img src={`${API}/items/${qrItem.id}/qrcode?taglia=${encodeURIComponent(s)}`} alt={`QR ${s}`}
                       className="w-32 h-32 mx-auto border border-slate-100 rounded" />
                  <a href={`${API}/items/${qrItem.id}/qrcode?taglia=${encodeURIComponent(s)}`}
                     download={`qr_${qrItem.code}_${s}.png`}>
                    <Button variant="outline" size="sm" className="mt-2 w-full">Scarica</Button>
                  </a>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Una voce della scheda. Sta fuori dal dialogo di proposito: un componente
// definito dentro un altro viene ricreato a ogni ridisegno.
function Riga({ nome, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="text-xs uppercase tracking-wider text-slate-400">{nome}</div>
      <div className="col-span-2 text-sm">{children}</div>
    </div>
  );
}

// La scheda tecnica del fornitore: quello che serve sapere prima di
// consegnare un DPI, e che finora stava solo nel PDF dell'offerta.
function SchedaTecnica({ item, onClose }) {
  const s = item.scheda || {};
  const cat = CATEGORIA_DPI[s.categoria_dpi];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader>

        <div className="flex gap-4 items-start flex-wrap">
          {item.photo_url && (
            <img src={photoUrl(item.photo_url)} alt={item.name}
                 className="w-40 h-40 object-contain rounded border border-slate-200 bg-white flex-shrink-0" />
          )}
          <div className="flex-1 min-w-[200px] space-y-2">
            <div className="font-mono text-xs text-slate-500">{item.code}</div>
            <div className="flex flex-wrap gap-1.5 items-center">
              <Badge variant="outline">{item.category}</Badge>
              {cat && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${cat.classe}`}>
                  {cat.nome} · {cat.spiega}
                </span>
              )}
            </div>
            {(s.norme || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {s.norme.map(n => (
                  <span key={n} className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#0067b2] text-[#0067b2] bg-sky-50">
                    {n}
                  </span>
                ))}
              </div>
            )}
            {s.prezzo !== null && s.prezzo !== undefined && (
              <div className="text-sm">
                <span className="font-display text-2xl font-black">{euro(s.prezzo)}</span>
                <span className="text-xs text-slate-500"> · prezzo di offerta, IVA esclusa</span>
              </div>
            )}
          </div>
        </div>

        {s.descrizione && (
          <p className="text-sm text-slate-600 leading-relaxed">{s.descrizione}</p>
        )}

        <div className="border border-slate-200 rounded px-3">
          {s.materiali && <Riga nome="Materiali">{s.materiali}</Riga>}
          {s.grammatura && <Riga nome="Grammatura">{s.grammatura}</Riga>}
          {s.colori && <Riga nome="Colore">{s.colori}</Riga>}
          {s.chiusura && <Riga nome="Chiusura">{s.chiusura}</Riga>}
          {s.bande_rifrangenti && <Riga nome="Bande rifrangenti">{s.bande_rifrangenti}</Riga>}
          {item.sizes?.length > 0 && (
            <Riga nome="Taglie">
              <div className="flex flex-wrap gap-1">
                {item.sizes.map(t => (
                  <span key={t} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t}</span>
                ))}
              </div>
            </Riga>
          )}
          {s.pezzi_per_cartone && <Riga nome="Per cartone">{s.pezzi_per_cartone} pezzi</Riga>}
          {s.manutenzione && <Riga nome="Manutenzione">{s.manutenzione}</Riga>}
          {s.fornitore && (
            <Riga nome="Fornitore">
              {s.fornitore}{s.famiglia ? ` · ${s.famiglia.toLowerCase()}` : ""}
            </Riga>
          )}
        </div>

        {(s.dettagli || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Caratteristiche</div>
            <ul className="text-sm space-y-1">
              {s.dettagli.map((d, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-orange-500 flex-shrink-0">·</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(s.settori || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Settori d&apos;utilizzo</div>
            <div className="flex flex-wrap gap-1">
              {s.settori.map(x => (
                <span key={x} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{x}</span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// L'inventario vero: si stampa il foglio, si conta in magazzino con la penna
// in mano, si ribattono i numeri e si ricarica. Le giacenze diventano quelle
// contate, ma solo dopo aver visto l'elenco delle differenze.
function DialogInventario({ puoCaricare, onClose, onImportato }) {
  const [file, setFile] = useState(null);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);

  const scarica = async (percorso, nome) => {
    try {
      const r = await authFetch(percorso);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "");
      }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || "Non riesco a preparare il foglio");
    }
  };

  const manda = async (soloVerifica) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("solo_verifica", soloVerifica ? "true" : "false");
      const { data } = await api.post("/inventario/importa", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`Giacenze aggiornate: ${data.applicate} righe`);
        onImportato();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella lettura del foglio");
    } finally { setBusy(false); }
  };

  const oggi = new Date().toISOString().slice(0, 10);
  // Si applica solo dopo aver guardato: l'anteprima accende il pulsante.
  const verificato = !!esito && esito.solo_verifica;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Inventario DPI</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="border border-slate-200 rounded p-4">
            <div className="font-semibold text-sm mb-1">1 · Stampa il foglio e conta</div>
            <p className="text-xs text-slate-600 mb-3">
              Il foglio ha tutti gli articoli e tutte le taglie, con un riquadro
              vuoto per ogni riga. La giacenza che risulta all&apos;app non è
              stampata apposta: se c&apos;è già scritta, si tende a confermarla
              invece di contare.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => scarica(
                "/inventario/foglio.pdf", `Inventario_DPI_${oggi}.pdf`)}>
                <Download className="w-4 h-4 mr-2" /> Foglio da stampare
              </Button>
              <Button variant="outline" onClick={() => scarica(
                "/inventario/foglio.xlsx", `Inventario_DPI_${oggi}.xlsx`)}>
                <Download className="w-4 h-4 mr-2" /> Foglio Excel
              </Button>
            </div>
          </div>

          {puoCaricare && (
            <div className="border border-slate-200 rounded p-4">
              <div className="font-semibold text-sm mb-1">2 · Ricarica quello che hai contato</div>
              <p className="text-xs text-slate-600 mb-3">
                Riporta i numeri del foglio di carta nella colonna «Contato» del
                foglio Excel, poi caricalo qui. Le righe lasciate in bianco
                restano come stanno: non diventano zero.
              </p>
              <Input type="file" accept=".xlsx"
                     onChange={(e) => { setFile(e.target.files?.[0] || null); setEsito(null); }} />
            </div>
          )}

          {esito && (
            <div className="border border-slate-200 rounded p-4 space-y-3">
              <div className="text-sm">
                Righe contate: <strong>{esito.lette}</strong> · già giuste:{" "}
                <strong>{esito.invariate}</strong> · lasciate in bianco:{" "}
                <strong>{esito.non_contate}</strong>
              </div>

              {esito.differenze?.length > 0 ? (
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Articolo</th>
                        <th className="text-left p-2">Taglia</th>
                        <th className="text-right p-2">Era</th>
                        <th className="text-right p-2">Contato</th>
                        <th className="text-right p-2">Differenza</th>
                      </tr>
                    </thead>
                    <tbody>
                      {esito.differenze.map((d, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="p-2">
                            {d.articolo}
                            {d.taglia_nuova && (
                              <Badge variant="outline" className="ml-2 text-[10px]">taglia nuova</Badge>
                            )}
                          </td>
                          <td className="p-2">{d.taglia}</td>
                          <td className="p-2 text-right text-slate-500">{d.prima}</td>
                          <td className="p-2 text-right font-semibold">{d.contato}</td>
                          <td className={`p-2 text-right font-semibold ${
                            d.differenza < 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {d.differenza > 0 ? `+${d.differenza}` : d.differenza}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-slate-600">
                  Nessuna differenza: quello che hai contato coincide con l&apos;archivio.
                </div>
              )}

              {esito.sconosciute?.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <strong>{esito.sconosciute.length} righe con un codice che non esiste</strong> —
                  non verranno caricate: {esito.sconosciute.map(s => s.codice).join(", ")}
                </div>
              )}

              {esito.avvisi?.length > 0 && (
                <ul className="text-xs text-amber-700 list-disc pl-5 space-y-1">
                  {esito.avvisi.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          {puoCaricare && (
            <>
              <Button variant="outline" onClick={() => manda(true)} disabled={busy || !file}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Controlla
              </Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={() => manda(false)} disabled={busy || !file || !verificato}>
                Aggiorna le giacenze
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// I due CSV del magazzino virtuale: prima si guarda cosa succederebbe, poi
// si applica. Non c'e' un passaggio in cui si scrive senza aver visto.
function DialogImportCsv({ onClose, onImportato }) {
  const [tipo, setTipo] = useState("giacenze");
  const [file, setFile] = useState(null);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);
  // Chi non si trova da solo lo si abbina a mano: nome nel file -> dipendente.
  const [aMano, setAMano] = useState({});
  const [persone, setPersone] = useState([]);
  // Stessa cosa per gli articoli, che nei due sistemi hanno nomi diversi.
  const [articoliAMano, setArticoliAMano] = useState({});
  const [articoli, setArticoli] = useState([]);

  const rotta = tipo === "giacenze" ? "/items/import-giacenze" : "/deliveries/import-storico";

  // Per la scelta a mano servono l'anagrafica intera, ex dipendenti compresi,
  // e l'elenco degli articoli in magazzino.
  useEffect(() => {
    if (tipo !== "consegne" || persone.length) return;
    Promise.all([
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/employees/archived").catch(() => ({ data: [] })),
      api.get("/items").catch(() => ({ data: [] })),
    ]).then(([a, b, c]) => {
      const tutte = [
        ...(a.data || []).map(p => ({ ...p, attivo: true })),
        ...(b.data || []).map(p => ({ ...p, attivo: false })),
      ];
      tutte.sort((x, y) => (x.full_name || "").localeCompare(y.full_name || ""));
      setPersone(tutte);
      setArticoli([...(c.data || [])].sort(
        (x, y) => (x.name || "").localeCompare(y.name || "")));
    });
  }, [tipo, persone.length]);

  const manda = async (soloVerifica) => {
    if (!file) { toast.error("Scegli il file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("solo_verifica", soloVerifica ? "true" : "false");
      const scelte = Object.fromEntries(
        Object.entries(aMano).filter(([, v]) => v));
      const scelteArt = Object.fromEntries(
        Object.entries(articoliAMano).filter(([, v]) => v));
      if (tipo === "consegne" && Object.keys(scelte).length) {
        fd.append("abbinamenti", JSON.stringify(scelte));
      }
      if (tipo === "consegne" && Object.keys(scelteArt).length) {
        fd.append("abbinamenti_articoli", JSON.stringify(scelteArt));
      }
      const { data } = await api.post(rotta, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(tipo === "giacenze"
          ? `${data.creati} articoli creati, ${data.aggiornati} aggiornati`
          : `${data.importate} consegne portate in archivio`);
        onImportato();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const cambiaTipo = (t) => {
    setTipo(t); setFile(null); setEsito(null); setAMano({}); setArticoliAMano({});
  };
  const verificato = esito && esito.solo_verifica;
  const nonTrovate = esito?.persone_non_trovate || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Importa dal magazzino virtuale</DialogTitle></DialogHeader>

        <div className="flex gap-2">
          {[["giacenze", "Giacenze", "inventory.csv"],
            ["consegne", "Storico consegne", "Ordini.csv"]].map(([k, nome, file_]) => (
            <button key={k} type="button" onClick={() => cambiaTipo(k)}
                    className={`flex-1 text-left px-3 py-2 rounded border ${
                      tipo === k ? "bg-[#0067b2] text-white border-[#0067b2]"
                                 : "bg-white text-slate-600 border-slate-200"}`}>
              <div className="text-sm font-semibold">{nome}</div>
              <div className={`text-[11px] font-mono ${tipo === k ? "text-sky-100" : "text-slate-400"}`}>
                {file_}
              </div>
            </button>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
          {tipo === "giacenze" ? (
            <>
              <strong className="text-amber-800">Le giacenze diventano quelle del file.</strong>{" "}
              Gli articoli che non esistono ancora vengono creati; per quelli già in
              elenco vengono riscritte taglie e quantità. Il nome e la categoria che
              hai scelto tu restano.
            </>
          ) : (
            <>
              <strong className="text-amber-800">Le consegne storiche non hanno firma</strong> —
              non c&apos;è mai stata — e restano marcate come importate. Le giacenze non
              vengono toccate: sono già scalate nel file dell&apos;inventario. Rilanciando
              non si duplicano.
            </>
          )}
        </div>

        <div>
          <Label>File CSV</Label>
          <Input type="file" accept=".csv"
                 onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
        </div>

        {/* --- esito della verifica: giacenze --- */}
        {esito && tipo === "giacenze" && verificato && (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-semibold">{esito.da_creare.length}</span> articoli nuovi,{" "}
              <span className="font-semibold">{esito.da_aggiornare.length}</span> già in elenco.
            </div>
            <div className="space-y-1 max-h-[34vh] overflow-y-auto">
              {[...esito.da_creare.map(a => ({ ...a, nuovo: true })),
                ...esito.da_aggiornare].map((a) => (
                <div key={a.nome} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-1.5">
                  <span className="text-sm flex-1 min-w-0 truncate">{a.nome}</span>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">{a.categoria}</Badge>
                  {a.nuovo
                    ? <span className="text-[10px] font-semibold text-emerald-700 flex-shrink-0">nuovo</span>
                    : <span className="text-[10px] text-slate-400 flex-shrink-0 tabular-nums">
                        {a.totale_attuale} → {a.totale}
                      </span>}
                  <span className="text-xs tabular-nums text-slate-600 w-14 text-right flex-shrink-0">
                    {a.totale} pz
                  </span>
                  {a.da_contare?.length > 0 && (
                    <span className="text-[10px] text-amber-700 flex-shrink-0"
                          title={`Senza quantità: ${a.da_contare.join(", ")}`}>
                      {a.da_contare.length} da contare
                    </span>
                  )}
                </div>
              ))}
            </div>
            {esito.avvisi?.length > 0 && (
              <p className="text-xs text-amber-700">
                {esito.avvisi.length} righe hanno la giacenza scritta &quot;In Stock&quot; invece
                di un numero: entrano a zero e vanno contate.
              </p>
            )}
          </div>
        )}

        {/* --- esito della verifica: consegne --- */}
        {esito && tipo === "consegne" && verificato && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[["Nel file", esito.consegne_nel_file], ["Abbinate", esito.abbinate],
                ["Da importare", esito.da_importare], ["A ex dipendenti", esito.ex_dipendenti]]
                .map(([n, v]) => (
                <div key={n} className="border border-slate-200 rounded p-2">
                  <div className="font-display text-xl font-black tabular-nums">{v}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{n}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Consegne dal {esito.dal} al {esito.al}.
              {esito.gia_importate > 0 && ` ${esito.gia_importate} erano già state importate.`}
            </p>

            {nonTrovate.length > 0 && (
              <div className="border border-amber-200 bg-amber-50/60 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <UserX className="w-4 h-4 text-amber-700" />
                  <span className="font-semibold text-sm">
                    {nonTrovate.length} persone da verificare
                  </span>
                </div>
                <p className="text-xs text-slate-600 mb-2">
                  Non le trovo da solo. Scegli tu la persona giusta — l&apos;elenco
                  comprende anche gli ex dipendenti — e rilancia la verifica. Chi resta
                  senza nome non viene importato.
                </p>
                <div className="space-y-2 max-h-[36vh] overflow-y-auto">
                  {nonTrovate.map((p) => (
                    <div key={p.nome}
                         className="bg-white border border-slate-200 rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm flex-1 min-w-0">
                          {p.nome}
                          {p.varianti?.length > 1 && (
                            <span className="text-[11px] text-slate-400">
                              {" "}(anche: {p.varianti.slice(1).join(", ")})
                            </span>
                          )}
                        </span>
                        {p.motivo !== "assente" && (
                          <span className="text-[10px] font-semibold text-amber-700 flex-shrink-0">
                            {p.motivo === "omonimi" ? "due omonimi" : "nome ambiguo"}
                          </span>
                        )}
                        <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">
                          {p.consegne} consegne
                        </span>
                      </div>

                      {p.suggerimenti?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <span className="text-[11px] text-slate-500">Forse è:</span>
                          {p.suggerimenti.map((s) => (
                            <button key={s.id} type="button"
                                    onClick={() => setAMano({ ...aMano, [p.nome]: s.id })}
                                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                      aMano[p.nome] === s.id
                                        ? "bg-[#0067b2] text-white border-[#0067b2]"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                              {s.full_name}
                              {!s.attivo && (
                                <span className={aMano[p.nome] === s.id ? "text-sky-100" : "text-slate-400"}>
                                  {" "}· in archivio
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      <select className="w-full h-8 border border-slate-200 rounded px-2 text-xs mt-1.5"
                              value={aMano[p.nome] || ""}
                              onChange={(e) => setAMano({ ...aMano, [p.nome]: e.target.value })}>
                        <option value="">— non importare le sue consegne —</option>
                        {persone.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.full_name}{x.attivo ? "" : " · in archivio"}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                {Object.values(aMano).filter(Boolean).length > 0 && (
                  <p className="text-xs text-[#0067b2] font-medium mt-2">
                    {Object.values(aMano).filter(Boolean).length} abbinate a mano:
                    rilancia la Verifica per vederne l&apos;effetto.
                  </p>
                )}
              </div>
            )}

            {esito.articoli_non_trovati?.length > 0 && (
              <div className="border border-red-200 bg-red-50/60 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-sm">Articoli non in magazzino</span>
                </div>
                <p className="text-xs text-slate-600">
                  Il negozio e il magazzino chiamano le cose in modo diverso. Scegli
                  tu a quale articolo corrispondono e rilancia la verifica; quelli
                  lasciati vuoti non vengono importati.
                </p>
                <div className="space-y-2 mt-2 max-h-[30vh] overflow-y-auto">
                  {esito.articoli_non_trovati.map((a) => (
                    <div key={a.nome} className="bg-white border border-slate-200 rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm flex-1 min-w-0 truncate">{a.nome}</span>
                        <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">
                          {a.righe} righe
                        </span>
                      </div>
                      {a.suggerimenti?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          <span className="text-[11px] text-slate-500">Forse è:</span>
                          {a.suggerimenti.map((s) => (
                            <button key={s.id} type="button"
                                    onClick={() => setArticoliAMano({ ...articoliAMano, [a.nome]: s.id })}
                                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                      articoliAMano[a.nome] === s.id
                                        ? "bg-[#0067b2] text-white border-[#0067b2]"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                              {s.name}
                            </button>
                          ))}
                        </div>
                      )}
                      <select className="w-full h-8 border border-slate-200 rounded px-2 text-xs mt-1.5"
                              value={articoliAMano[a.nome] || ""}
                              onChange={(e) => setArticoliAMano({ ...articoliAMano, [a.nome]: e.target.value })}>
                        <option value="">— non importare queste righe —</option>
                        {articoli.map((x) => (
                          <option key={x.id} value={x.id}>{x.name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {esito && !verificato && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
            {tipo === "giacenze"
              ? `Fatto: ${esito.creati} articoli creati, ${esito.aggiornati} aggiornati.`
              : `Fatto: ${esito.importate} consegne portate in archivio.`}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button variant="outline" onClick={() => manda(true)} disabled={busy || !file}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Verifica
          </Button>
          <Button onClick={() => manda(false)} disabled={busy || !file || !verificato}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogCatalogo({ onClose, onImportato }) {
  const [dati, setDati] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/items/catalogo")
      .then(({ data }) => setDati(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Errore"));
  }, []);

  const nuovi = (dati?.articoli || []).filter(a => !a.gia_presente);
  const esistenti = (dati?.articoli || []).filter(a => a.gia_presente);

  const importa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/items/catalogo");
      toast.success(`${data.creati} articoli creati, ${data.aggiornati} aggiornati`
        + (data.foto_tenute ? ` · ${data.foto_tenute} foto già presenti lasciate com'erano` : ""));
      onImportato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Catalogo del fornitore</DialogTitle></DialogHeader>
        {!dati && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}
        {dati && (
          <>
            <p className="text-sm text-slate-600">
              Offerta <span className="font-mono">{dati.offerta}</span> di {dati.fornitore}.
              Ogni articolo porta con sé foto, norme, materiali, taglie e prezzo.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
              <strong className="text-amber-800">Le giacenze non si toccano.</strong>{" "}
              Gli articoli già in magazzino ricevono solo la scheda tecnica e la foto;
              quelli nuovi nascono con giacenza zero e scorta minima zero, da impostare
              come vuoi. Si può rilanciare quante volte serve.
            </div>

            {nuovi.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                  {nuovi.length} articoli nuovi
                </div>
                <div className="space-y-1 max-h-[32vh] overflow-y-auto">
                  {nuovi.map(a => (
                    <div key={a.codice} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-1.5">
                      <span className="font-mono text-[11px] text-slate-500 w-20 flex-shrink-0">{a.codice}</span>
                      <span className="text-sm flex-1 min-w-0 truncate">{a.nome}</span>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{a.categoria}</Badge>
                      {a.categoria_dpi && CATEGORIA_DPI[a.categoria_dpi] && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 ${CATEGORIA_DPI[a.categoria_dpi].classe}`}>
                          {CATEGORIA_DPI[a.categoria_dpi].nome}
                        </span>
                      )}
                      <span className="text-xs tabular-nums text-slate-500 w-16 text-right flex-shrink-0">
                        {euro(a.prezzo) || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {esistenti.length > 0 && (
              <p className="text-xs text-slate-500">
                {esistenti.length} {esistenti.length === 1 ? "articolo è già" : "articoli sono già"} in
                magazzino: {esistenti.map(a => a.codice).join(", ")}. Riceve
                {esistenti.length === 1 ? "" : "ranno"} solo la scheda tecnica.
              </p>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={importa} disabled={busy || !dati}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importazione...</>
                  : <><Download className="w-4 h-4 mr-2" /> Importa</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemForm({ initial, onSubmit, onClose }) {
  const [form, setForm] = useState(initial || {
    code: "", name: "", category: "Mani", sizes: ["S","M","L","XL"], min_stock: 1, icon: "Package",
  });
  const [sizesText, setSizesText] = useState((initial?.sizes || ["S","M","L","XL"]).join(","));

  const submit = (e) => {
    e.preventDefault();
    const sizes = sizesText.split(",").map(s => s.trim()).filter(Boolean);
    onSubmit({ ...form, sizes });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Codice</Label><Input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})} required disabled={!!initial} /></div>
          <div><Label>Nome articolo</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required /></div>
          <div><Label>Categoria</Label>
            <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Taglie (separate da virgola)</Label><Input value={sizesText} onChange={(e)=>setSizesText(e.target.value)} placeholder="S,M,L,XL o 38,39,40" /></div>
          <div><Label>Scorta minima per allerta</Label><Input type="number" min="0" value={form.min_stock} onChange={(e)=>setForm({...form, min_stock: parseInt(e.target.value)||0})} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ item, onClose, onAdjust }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Carico/Scarico: {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {item.sizes.map(s => {
            const q = item.stock?.[s] || 0;
            return <div key={s} className="flex items-center justify-between p-3 border border-slate-200 rounded">
              <div>
                <div className="font-semibold">Taglia {s}</div>
                <div className="text-xs text-slate-500">In magazzino: <strong>{q}</strong></div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => onAdjust(s, -1)}><Minus className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => onAdjust(s, -5)}>-5</Button>
                <Button size="sm" variant="outline" onClick={() => onAdjust(s, 1)}><Plus className="w-4 h-4" /></Button>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => onAdjust(s, 5)}>+5</Button>
              </div>
            </div>;
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
